// API response types
export type ApiResponse<T> = {
  data: T;
  message?: string;
  success: boolean;
};

/**
 * Error info returned inline (in an otherwise-200 response) when a backend
 * endpoint's call to the AI engine failed but the endpoint degraded
 * gracefully instead of rejecting the whole request.
 */
export type AiErrorInfo = {
  code?: string;
  message: string;
  retryable: boolean;
  retryAfter: number | null;
  requestId: string | null;
};

export type ApiError = {
  message: string;
  statusCode: number;
  errors?: unknown;
  code?: string;
  retryable?: boolean;
  retryAfter?: number | null;
  requestId?: string | null;
};

export type PaginatedResponse<T> = {
  data: T[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
};

export type ApiErrorDetails = {
  message: string;
  fieldErrors: string[];
  code?: string;
  retryable?: boolean;
  retryAfter?: number | null;
  requestId?: string | null;
};

function isApiError(error: unknown): error is ApiError {
  return (
    typeof error === 'object' &&
    error !== null &&
    'message' in error &&
    'statusCode' in error
  );
}

/**
 * Normalizes the `errors` payload from the API into a flat string list.
 *
 * Handles three server formats:
 *  - Array of objects: `[{ message: "..." }, ...]`
 *  - Array of strings: `["...", ...]`
 *  - Record of arrays:  `{ field: ["msg", ...], ... }`
 */
function normalizeErrorDetails(errors: unknown): string[] {
  if (!errors) return [];

  if (Array.isArray(errors)) {
    return errors
      .map((entry) => {
        if (typeof entry === 'string') return entry;
        if (typeof entry === 'object' && entry !== null && 'message' in entry) {
          return String((entry as { message: unknown }).message);
        }
        return null;
      })
      .filter((v): v is string => v !== null);
  }

  if (typeof errors === 'object') {
    return Object.entries(errors as Record<string, unknown>).flatMap(
      ([field, messages]) => {
        if (Array.isArray(messages)) {
          return messages.map((msg) => `${field}: ${msg}`);
        }
        return [`${field}: ${String(messages)}`];
      },
    );
  }

  return [];
}

/**
 * Extracts a user-friendly error message and field-level details
 * from the API error format `{ message, statusCode, errors }` produced
 * by the axios response interceptor. Also handles native Error instances
 * and unknown throw values.
 */
export function extractApiError(error: unknown): ApiErrorDetails {
  if (error instanceof ApiRequestError) {
    return {
      message: error.message,
      fieldErrors: [],
      code: error.code,
      retryable: error.retryable,
      retryAfter: error.retryAfter,
      requestId: error.requestId,
    };
  }

  if (isApiError(error)) {
    const fieldErrors = normalizeErrorDetails(error.errors);
    return {
      message: error.message,
      fieldErrors,
      code: error.code,
      retryable: error.retryable,
      retryAfter: error.retryAfter,
      requestId: error.requestId,
    };
  }

  if (error instanceof Error) {
    return { message: error.message, fieldErrors: [] };
  }

  return { message: 'An unexpected error occurred. Please try again.', fieldErrors: [] };
}

type AiErrorEnvelope = {
  detail?: string;
  error?: {
    code?: string;
    message?: string;
    retryable?: boolean;
    retry_after?: number | null;
    request_id?: string | null;
  };
};

/**
 * Error thrown by raw `fetch`-based AI calls, carrying the same
 * `code` / `retryable` / `retryAfter` / `requestId` fields the axios
 * interceptor attaches to `ApiError` so UI code can branch on them
 * regardless of which transport was used.
 */
export class ApiRequestError extends Error {
  code?: string;
  retryable: boolean;
  retryAfter: number | null;
  requestId: string | null;
  statusCode?: number;

  constructor(
    message: string,
    options: { code?: string; retryable?: boolean; retryAfter?: number | null; requestId?: string | null; statusCode?: number } = {},
  ) {
    super(message);
    this.name = 'ApiRequestError';
    this.code = options.code;
    this.retryable = options.retryable ?? false;
    this.retryAfter = options.retryAfter ?? null;
    this.requestId = options.requestId ?? null;
    this.statusCode = options.statusCode;
  }
}

/**
 * Parses the AI engine's error envelope (`{ detail, error: { message, code,
 * retryable, retry_after, request_id } }`) from a failed `fetch` `Response`
 * and returns a populated `ApiRequestError`, falling back to `fallbackMessage`
 * when the body can't be parsed.
 */
export async function extractFetchError(response: Response, fallbackMessage: string): Promise<ApiRequestError> {
  const body = (await response.json().catch(() => null)) as AiErrorEnvelope | null;
  const message = body?.error?.message || body?.detail || fallbackMessage;
  return new ApiRequestError(message, {
    code: body?.error?.code,
    retryable: body?.error?.retryable ?? false,
    retryAfter: body?.error?.retry_after ?? null,
    requestId: body?.error?.request_id ?? null,
    statusCode: response.status,
  });
}

